/**
 * AccountSelector
 *
 * Dropdown para elegir desde qué cuenta de WhatsApp sale una campaña.
 * Solo muestra cuentas con status === 'connected'. Las disconnected/pending
 * aparecen como "no disponibles" en gris (no se pueden elegir).
 *
 * Si `accounts` está vacío → emite un empty state inline con CTA al flow
 * de vinculación.
 *
 * Pre-selecciona automáticamente la cuenta marcada como default si el
 * caller no especifica selectedId.
 */

import { Link } from "react-router-dom";
import {
    Check,
    ChevronDown,
    Smartphone,
    Star,
    AlertCircle,
    Plus,
    Globe,
    MapPin,
    Settings,
    Rocket,
    Pause,
    Play,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Card } from "../../../shared/components/ui/Card";
import type {
    WhatsAppAccount,
    WhatsAppHealthRating,
    WhatsAppActivationStatus,
} from "../types";

const HEALTH_DOT: Record<WhatsAppHealthRating, string> = {
    green: "bg-emerald-500",
    yellow: "bg-amber-500",
    red: "bg-red-500",
};

const ACTIVATION_BADGE: Record<
    WhatsAppActivationStatus,
    { label: string; bg: string; text: string; icon: React.ReactNode }
> = {
    unconfigured: {
        label: "Sin setup",
        bg: "bg-violet-50",
        text: "text-violet-700",
        icon: <Settings size={8} />,
    },
    warming_up: {
        label: "Warm-up",
        bg: "bg-orange-50",
        text: "text-orange-700",
        icon: <Rocket size={8} />,
    },
    active: {
        label: "Activa",
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        icon: <Play size={8} fill="currentColor" />,
    },
    paused: {
        label: "Pausada",
        bg: "bg-slate-100",
        text: "text-slate-600",
        icon: <Pause size={8} fill="currentColor" />,
    },
};

interface AccountSelectorProps {
    accounts: WhatsAppAccount[];
    selectedId?: string;
    onSelect: (id: string) => void;
    loading?: boolean;
}

export default function AccountSelector({
    accounts,
    selectedId,
    onSelect,
    loading = false,
}: AccountSelectorProps) {
    const [open, setOpen] = useState(false);

    /** Una cuenta es seleccionable solo si está conectada Y puede enviar (active o warming_up) */
    const isSelectable = (a: WhatsAppAccount): boolean => {
        return (
            a.status === "connected" &&
            (a.activationStatus === "active" ||
                a.activationStatus === "warming_up")
        );
    };

    // Auto-select default si no hay seleccionada
    useEffect(() => {
        if (selectedId) return;
        if (loading) return;
        const def = accounts.find((a) => a.isDefault && isSelectable(a));
        if (def) {
            onSelect(def.id);
            return;
        }
        const firstSelectable = accounts.find(isSelectable);
        if (firstSelectable) onSelect(firstSelectable.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts, loading]);

    const selected = accounts.find((a) => a.id === selectedId);
    const selectableCount = accounts.filter(isSelectable).length;
    const hasAny = accounts.length > 0;

    /* ─── Empty state: 0 cuentas vinculadas ─── */
    if (!loading && !hasAny) {
        return (
            <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50/50 to-white">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg text-amber-700">
                        <Smartphone size={18} />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base font-bold text-slate-900 mb-1">
                            Vinculá un WhatsApp primero
                        </h3>
                        <p className="text-xs text-slate-600 mb-3">
                            Para hacer envíos necesitás al menos una cuenta de
                            WhatsApp escaneada. Toma menos de 30 segundos.
                        </p>
                        <Link
                            to="/whatsapp/accounts"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold rounded-lg transition-colors"
                        >
                            <Plus size={12} />
                            Vincular ahora
                        </Link>
                    </div>
                </div>
            </Card>
        );
    }

    /* ─── Empty state: cuentas existen pero ninguna usable ─── */
    if (!loading && hasAny && selectableCount === 0) {
        const onlyUnconfigured = accounts.every(
            (a) => a.activationStatus === "unconfigured",
        );
        const onlyPaused = accounts.every(
            (a) => a.activationStatus === "paused",
        );
        return (
            <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50/50 to-white">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg text-amber-700">
                        {onlyUnconfigured ? (
                            <Settings size={18} />
                        ) : (
                            <AlertCircle size={18} />
                        )}
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base font-bold text-slate-900 mb-1">
                            {onlyUnconfigured
                                ? "Falta completar setup"
                                : onlyPaused
                                  ? "Todas tus cuentas están pausadas"
                                  : "Ninguna cuenta disponible para enviar"}
                        </h3>
                        <p className="text-xs text-slate-600 mb-3">
                            {onlyUnconfigured
                                ? "Terminá la configuración de al menos una cuenta para poder hacer envíos."
                                : onlyPaused
                                  ? "Reactivá una cuenta para hacer envíos. Revisá la salud antes."
                                  : "Reconectá una cuenta o reactivá una pausada."}
                        </p>
                        <Link
                            to="/whatsapp/accounts"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold rounded-lg transition-colors"
                        >
                            Ir a cuentas
                        </Link>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="text-sm font-bold text-slate-900">
                        Enviar desde
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                        Elegí desde qué número sale el envío
                    </p>
                </div>
                <Link
                    to="/whatsapp/accounts"
                    className="text-[11px] text-violet-600 hover:text-violet-800 underline underline-offset-2"
                >
                    Gestionar cuentas
                </Link>
            </div>

            <div className="relative">
                <button
                    onClick={() => setOpen((o) => !o)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
                >
                    <div className="flex items-center gap-3 min-w-0 text-left">
                        <div className="w-9 h-9 rounded-lg bg-green-100 text-green-700 flex items-center justify-center flex-shrink-0 relative">
                            <Smartphone size={16} />
                            {selected?.health && (
                                <span
                                    className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${
                                        HEALTH_DOT[selected.health.rating]
                                    } ring-2 ring-white`}
                                    title={`Salud: ${selected.health.score}/100`}
                                />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                                <span className="text-sm font-semibold text-slate-900 truncate">
                                    {selected?.displayName || "Elegí una cuenta"}
                                </span>
                                {selected?.isDefault && (
                                    <Star
                                        size={10}
                                        className="text-violet-500"
                                        fill="currentColor"
                                    />
                                )}
                                {selected?.scope === "global" && (
                                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 flex items-center gap-0.5">
                                        <Globe size={8} />
                                        Global
                                    </span>
                                )}
                            </div>
                            <p className="text-[11px] text-slate-500 font-mono mt-0.5">
                                {selected?.phoneNumber || "—"}
                            </p>
                        </div>
                    </div>
                    <ChevronDown
                        size={16}
                        className={`text-slate-400 transition-transform ${
                            open ? "rotate-180" : ""
                        }`}
                    />
                </button>

                {open && (
                    <>
                        <div
                            className="fixed inset-0 z-30"
                            onClick={() => setOpen(false)}
                        />
                        <div className="absolute left-0 right-0 top-full mt-1 z-40 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                            {accounts.map((a) => {
                                const selectable = isSelectable(a);
                                const isSelected = a.id === selectedId;
                                const actBadge = ACTIVATION_BADGE[a.activationStatus];
                                return (
                                    <button
                                        key={a.id}
                                        disabled={!selectable}
                                        onClick={() => {
                                            onSelect(a.id);
                                            setOpen(false);
                                        }}
                                        className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 border-b border-slate-100 last:border-0 transition-colors ${
                                            selectable
                                                ? "hover:bg-slate-50 cursor-pointer"
                                                : "opacity-50 cursor-not-allowed"
                                        } ${isSelected ? "bg-violet-50" : ""}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {a.health && (
                                                    <span
                                                        className={`w-2 h-2 rounded-full ${
                                                            HEALTH_DOT[a.health.rating]
                                                        } flex-shrink-0`}
                                                        title={`Salud: ${a.health.score}/100`}
                                                    />
                                                )}
                                                <span className="text-sm font-semibold text-slate-900 truncate">
                                                    {a.displayName}
                                                </span>
                                                {a.isDefault && (
                                                    <Star
                                                        size={9}
                                                        className="text-violet-500 flex-shrink-0"
                                                        fill="currentColor"
                                                    />
                                                )}
                                                {/* Activation badge — siempre visible */}
                                                <span
                                                    className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${actBadge.bg} ${actBadge.text} flex items-center gap-0.5`}
                                                >
                                                    {actBadge.icon}
                                                    {actBadge.label}
                                                </span>
                                                {a.scope === "global" ? (
                                                    <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 flex items-center gap-0.5">
                                                        <Globe size={7} />
                                                        Global
                                                    </span>
                                                ) : (
                                                    <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 flex items-center gap-0.5">
                                                        <MapPin size={7} />
                                                        Local
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <p className="text-[10px] text-slate-500 font-mono">
                                                    {a.phoneNumber}
                                                </p>
                                                {a.status !== "connected" && (
                                                    <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                                        {a.status === "pending_qr"
                                                            ? "Pending"
                                                            : a.status === "reconnecting"
                                                              ? "Reconect."
                                                              : "Desconectada"}
                                                    </span>
                                                )}
                                                {a.activationStatus === "warming_up" &&
                                                    a.warmupPlan && (
                                                        <span className="text-[9px] text-orange-600">
                                                            Límite hoy:{" "}
                                                            {
                                                                a.warmupPlan
                                                                    .dailyLimit
                                                            }{" "}
                                                            msj
                                                        </span>
                                                    )}
                                            </div>
                                        </div>
                                        {isSelected && (
                                            <Check
                                                size={14}
                                                className="text-violet-600 flex-shrink-0"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </Card>
    );
}
